import { Calculation, Raster } from "../../models/mapLayer";

type MapEntry = { Raster?: Raster; Calculation?: Calculation };

export interface VariableField {
  name: string;
  unit?: string;
}

export const VARIABLE_REGEX = /(\$[A-Za-z_][A-Za-z0-9_/]*)/g;

/** Extract map names referenced in a formula string (e.g. "$Net_Pay" → "Net Pay"). */
export function parseFormulaVars(expression: string): string[] {
  return [...expression.matchAll(VARIABLE_REGEX)].map((m) =>
    m[0].replace(/^\$/, "").replace(/_/g, " ")
  );
}

/**
 * Returns true if mapName is part of a cycle in the calculation map dependency graph.
 * Raster maps are leaf nodes — only calculation maps can form cycles.
 */
export function hasCircularReference(mapName: string, maps: MapEntry[]): boolean {
  const calcLookup = new Map<string, string>();
  maps.forEach((m) => {
    if (m.Calculation?.name) {
      calcLookup.set(m.Calculation.name, m.Calculation.transformer?.expression ?? "");
    }
  });

  const expr = calcLookup.get(mapName);
  if (!expr) return false;

  const visited = new Set<string>();

  function dfs(current: string): boolean {
    if (current === mapName) return true;
    if (visited.has(current)) return false;
    visited.add(current);
    const depExpr = calcLookup.get(current);
    if (!depExpr) return false;
    return parseFormulaVars(depExpr).some(dfs);
  }

  return parseFormulaVars(expr).some(dfs);
}

/** Deduplicated list of named variables from raster and calculation maps. */
export function buildAvailableVariables(maps: MapEntry[]): VariableField[] {
  const seen = new Set<string>();
  const candidates: VariableField[] = [
    ...maps
      .filter((m) => m.Raster?.name)
      .map((m) => ({ name: m.Raster?.name ?? "", unit: m.Raster?.unit })),
    ...maps
      .filter((m) => m.Calculation?.name)
      .map((m) => ({ name: m.Calculation?.name ?? "", unit: m.Calculation?.unit }))
  ];
  return candidates.filter((v) => {
    if (!v.name || seen.has(v.name)) return false;
    seen.add(v.name);
    return true;
  });
}
